import React, { useState, useCallback, useMemo } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useMailingLedgerData } from "../../hooks/useGenericLedgerData";
import { PpcFarmMailingLedgerData } from "../../types/ppcFarmTypes";
import PpcFarmMailingMetricsBar from "./PpcFarmMailingMetricsBar";
import GenericLedgerControlBar, {
  MAILING_LEDGER_FILTER_CONFIGS,
} from "./GenericLedgerControlBar";
import GenericLedgerDataTable, {
  MAILING_LEDGER_COLUMNS,
} from "./GenericLedgerDataTable";
import GenericLedgerDetailModal from "./GenericLedgerDetailModal";

const PpcFarmMailingLedgerClient: React.FC = () => {
  usePageTitle("Mailing Ledger | PPC Farm Mail Tracker");

  // Modal state
  const [selectedItem, setSelectedItem] =
    useState<PpcFarmMailingLedgerData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Use the custom hook for all data management
  const {
    data,
    statistics,
    filterOptions,
    loading,
    searchLoading,
    error,
    pagination,
    filters,
    sortOptions,
    setFilters,
    setSortOptions,
    loadMore,
  } = useMailingLedgerData({
    pageSize: 75,
    initialSort: { field: "dateEntered", direction: "desc" },
  });

  // Prepare filter configurations with actual options
  const filterConfigs = useMemo(() => {
    return MAILING_LEDGER_FILTER_CONFIGS.map((config) => ({
      ...config,
      options: filterOptions[config.key] || [],
    }));
  }, [filterOptions]);

  // Handle sort changes
  const handleSortChange = useCallback(
    (newSort: any) => {
      setSortOptions(newSort);
    },
    [setSortOptions],
  );

  // Handle infinite scroll load more
  const handleLoadMore = useCallback(() => {
    loadMore();
  }, [loadMore]);

  // Handle row click
  const handleRowClick = useCallback((item: PpcFarmMailingLedgerData) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedItem(null);
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Mailing Ledger
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track direct mail campaigns and mailing activities
        </p>
      </div>

      {/* Summary Metrics Bar */}
      <PpcFarmMailingMetricsBar loading={loading} error={error} />

      {/* Control Bar - Reduced spacing */}
      <div className="mt-4">
        <GenericLedgerControlBar
          filters={filters}
          onFiltersChange={setFilters}
          filterConfigs={filterConfigs}
          loading={false}
          totalResults={pagination.totalItems}
          searchPlaceholder="Search by Custom URL and notes..."
        />
      </div>

      {/* Data Table */}
      <GenericLedgerDataTable
        data={data}
        loading={loading}
        error={error}
        sortOptions={sortOptions}
        onSortChange={handleSortChange}
        onRowClick={handleRowClick}
        pagination={pagination}
        onLoadMore={handleLoadMore}
        columns={MAILING_LEDGER_COLUMNS}
        emptyStateMessage="No mailing records match your current filters. Try adjusting your search criteria."
      />

      {/* Detail Modal */}
      <GenericLedgerDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        data={selectedItem}
        ledgerType="mailing"
      />
    </div>
  );
};

export default PpcFarmMailingLedgerClient;
